'use client';

import { useState, useCallback, useEffect, useRef } from 'react';

import {
  getUserPoolTransactionsAction,
  I_UserPoolTransaction,
  I_UserPoolTransactionsResponse
} from '@/server/actions/user/getUserPoolTransactions.action';

interface I_UsePoolTransactionsProps {
  poolTokenSymbol?: string;
  poolAddress?: string;
  orderBy?: 'blockTimestamp';
  orderDirection?: 'asc' | 'desc';
  type?: 'BUY' | 'SELL';
  initialData?: I_UserPoolTransactionsResponse | null;
}

export const usePoolTransactions = ({
  poolTokenSymbol,
  poolAddress,
  orderBy = 'blockTimestamp',
  orderDirection = 'desc',
  type,
  initialData
}: I_UsePoolTransactionsProps = {}) => {
  const [data, setData] = useState<I_UserPoolTransaction[]>(
    initialData?.transactions || []
  );
  const [total, setTotal] = useState(initialData?.total || 0);
  const [hasMore, setHasMore] = useState(initialData?.hasMore || false);
  const [poolsData, setPoolsData] = useState(initialData?.pools || []);
  const [isLoading, setIsLoading] = useState(false);

  // Tracks the latest request to ignore stale async responses
  const requestIdRef = useRef(0);

  const fetchData = useCallback(
    async (offset: number, reset: boolean) => {
      // Capture current request ID to detect if a newer request supersedes this one
      const myRequestId = ++requestIdRef.current;

      setIsLoading(true);
      try {
        const response = await getUserPoolTransactionsAction({
          limit: 20,
          offset,
          poolTokenSymbol,
          poolAddress,
          orderBy,
          orderDirection,
          type
        });

        // Ignore response if a newer request has been started
        if (myRequestId !== requestIdRef.current) return;

        if (response?.data) {
          if (reset) {
            setData(response.data.transactions);
            // Update pools data when resetting (filter change)
            if (response.data.pools) {
              setPoolsData(response.data.pools);
            }
          } else {
            setData(prev => [...prev, ...response.data.transactions]);
          }
          setTotal(response.data.total);
          setHasMore(response.data.hasMore);
        }
      } catch (error) {
        if (myRequestId !== requestIdRef.current) return;
        console.error('Failed to fetch pool transactions:', error);
      } finally {
        if (myRequestId === requestIdRef.current) {
          setIsLoading(false);
        }
      }
    },
    [poolTokenSymbol, poolAddress, orderBy, orderDirection, type]
  );

  useEffect(() => {
    // Use initial data only if:
    // 1. We have initial data
    // 2. No filters are applied (no type filter)
    // 3. Using default sort (desc)
    const shouldUseInitialData =
      initialData && type === undefined && orderDirection === 'desc';

    if (shouldUseInitialData) {
      // Cancel any in-flight requests
      ++requestIdRef.current;
      // Use initial data, don't fetch
      setData(initialData.transactions);
      setTotal(initialData.total);
      setHasMore(initialData.hasMore);
      setPoolsData(initialData.pools || []);
      setIsLoading(false);
    } else {
      // Fetch new data when filters or sorting change
      // fetchData increments requestIdRef internally, so stale responses are ignored
      fetchData(0, true);
    }
  }, [
    fetchData,
    poolTokenSymbol,
    poolAddress,
    orderDirection,
    type,
    initialData
  ]);

  const handleLoadMore = useCallback(async () => {
    if (isLoading || !hasMore) return;
    await fetchData(data.length, false);
  }, [isLoading, hasMore, data.length, fetchData]);

  return {
    data,
    total,
    hasMore,
    poolsData,
    isLoading,
    handleLoadMore,
    refetch: () => fetchData(0, true)
  };
};
